import React from 'react';
import { useAppContext } from '@/context/AppContext';
import { Language } from '@/types';
import { TechnologiesData } from '@/data/technologies-data';
import styles from './About.module.scss';
import ImageComponent from '@/components/imageComponent/ImageComponent';
import Modal from '@/components/modal/Modal';
import {
  TECH_RADIUS_DESKTOP,
  TECH_RADIUS_MOBILE,
  TOOLS_RADIUS_DESKTOP,
  TOOLS_RADIUS_MOBILE,
} from '@/constants/skills';
import { ToolsData } from '@/data/tools-data';
import { credentialsData } from '@/data/credentials-data';

const About: React.FC = () => {
  const { language } = useAppContext();
  const [tooltip, setTooltip] = React.useState<{
    text: string;
    x: number;
    y: number;
    visible: boolean;
  }>({
    text: '',
    x: 0,
    y: 0,
    visible: false,
  });

  const [pauseTechnologies, setPauseTechnologies] = React.useState(false);
  const [pauseTools, setPauseTools] = React.useState(false);

  const [revealed, setRevealed] = React.useState({
    description: false,
    p1: false,
    p2: false,
    p3: false,
    technologies: false,
    tools: false,
    certifications: false,
  });

  const [isMobile, setIsMobile] = React.useState<boolean>(
    window.innerWidth <= 800,
  );

  const [modalImg, setModalImg] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (modalImg) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [modalImg]);

  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 800);
    window.addEventListener('resize', onResize, { passive: true } as any);
    return () => window.removeEventListener('resize', onResize as any);
  }, []);

  React.useEffect(() => {
    const hideAndResume = () => {
      setTooltip((prev) => ({ ...prev, visible: false }));
      setPauseTechnologies(false);
      setPauseTools(false);
    };

    const handleScroll = () => hideAndResume();
    const handleWheel = () => hideAndResume();
    const handleTouchMove = () => hideAndResume();
    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest('[data-tooltip]')) return;
      hideAndResume();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });

    const handleMouseLeave = () => hideAndResume();

    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const target = entry.target as HTMLElement;
          const section = target.dataset.reveal as
            | 'description'
            | 'p1'
            | 'p2'
            | 'p3'
            | 'certifications'
            | undefined;

          if (entry.isIntersecting && entry.intersectionRatio > 0.35) {
            if (section) {
              setRevealed((prev) => {
                const next = { ...prev, [section]: true } as typeof prev;
                if (section === 'p1' || (isMobile && section === 'p2')) {
                  next.technologies = true;
                  next.tools = true;
                }
                return next;
              });
            }
          }
        });
      },
      { threshold: 0.35, rootMargin: '0px 0px -12% 0px' },
    );

    const container = document.getElementById('about');
    const elements = container?.querySelectorAll('[data-reveal]');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [isMobile]);

  const technologies = React.useMemo(() => Object.values(TechnologiesData), []);
  const tools = React.useMemo(() => Object.values(ToolsData), []);

  const handleEnter = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
    label: string,
    setPause: (value: boolean) => void,
  ) => {
    setPause(true);
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    setTooltip({
      text: label,
      x: rect.left + rect.width / 2,
      y: rect.bottom + 4,
      visible: true,
    });
  };

  const handleLeave = (setPause: (value: boolean) => void) => {
    setPause(false);
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  return (
    <div className={styles.about} id="about">
      {tooltip.visible && (
        <div
          className={styles.globalTooltip}
          style={{
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          {tooltip.text}
        </div>
      )}
      <div className={styles.container}>
        {/* Left Side - Description */}
        <div
          className={`${styles.descriptionContainer} ${revealed.description ? styles.revealed : ''}`}
          data-reveal="description"
        >
          <div className={styles.descriptionText}>
            {language === Language.EN ? (
              <>
                <p
                  className={`${styles.paragraph} ${
                    revealed.p1 ? styles.revealed : ''
                  }`}
                  data-reveal="p1"
                >
                  I’m a <strong>front-end developer</strong> with a passion for
                  crafting <strong>seamless</strong> and{' '}
                  <strong>engaging</strong> user experiences. Whether it’s{' '}
                  <strong>designing intuitive interfaces</strong>,{' '}
                  <strong>optimizing performance</strong>, or{' '}
                  <strong>building smooth cross-platform applications</strong>,
                  I enjoy bringing ideas to life through <strong>clean</strong>{' '}
                  and <strong>creative code</strong>.
                </p>

                {/* Highlighted Section */}
                <p
                  className={`${styles.highlightedText} ${
                    revealed.p2 ? styles.revealed : ''
                  }`}
                  data-reveal="p2"
                >
                  With <strong>React</strong>, <strong>React Native</strong>,
                  and <strong>TypeScript</strong>, I focus on{' '}
                  <strong>user-centric development</strong>, solving UI
                  challenges, and refining interactions to make technology feel{' '}
                  <strong>effortless</strong>.
                </p>

                <p
                  className={`${styles.paragraph} ${
                    revealed.p3 ? styles.revealed : ''
                  }`}
                  data-reveal="p3"
                >
                  To me, <strong>development</strong> is more than just writing
                  code. It’s about shaping <strong>innovative ideas</strong>{' '}
                  into reality and creating experiences that leave a{' '}
                  <strong>lasting impact</strong> on users. Check out my{' '}
                  <strong>projects</strong> and connect with me through my{' '}
                  <strong>socials</strong>!
                </p>
              </>
            ) : (
              <>
                <p
                  className={`${styles.paragraph} ${
                    revealed.p1 ? styles.revealed : ''
                  }`}
                  data-reveal="p1"
                >
                  Eu sou um <strong>desenvolvedor front-end</strong> com paixão
                  por criar experiências de utilizador <strong>fluídas</strong>{' '}
                  e <strong>envolventes</strong>. Seja a{' '}
                  <strong>criar interfaces intuitivas</strong>,{' '}
                  <strong>otimizar o desempenho</strong> ou a{' '}
                  <strong>
                    desenvolver aplicações multiplataforma eficientes
                  </strong>
                  , gosto de dar vida às ideias através de{' '}
                  <strong>código limpo</strong> e <strong>criativo</strong>.
                </p>

                {/* Highlighted Section */}
                <p
                  className={`${styles.highlightedText} ${
                    revealed.p2 ? styles.revealed : ''
                  }`}
                  data-reveal="p2"
                >
                  Com <strong>React</strong>, <strong>React Native</strong> e{' '}
                  <strong>TypeScript</strong>, foco-me no{' '}
                  <strong>desenvolvimento centrado ao utilizador</strong>,
                  resolvendo desafios de UI e refinando interações para tornar a
                  tecnologia <strong>intuitiva e acessível</strong>.
                </p>

                <p
                  className={`${styles.paragraph} ${
                    revealed.p3 ? styles.revealed : ''
                  }`}
                  data-reveal="p3"
                >
                  Para mim, o <strong>desenvolvimento</strong> vai além da
                  simples escrita de código. Trata-se de transformar{' '}
                  <strong>ideias inovadoras</strong> em realidade e criar
                  experiências que deixem um <strong>impacto duradouro</strong>{' '}
                  nos utilizadores. Explora os meus <strong>projetos</strong> e
                  entra em contacto comigo através das minhas{' '}
                  <strong>redes sociais</strong>!
                </p>
              </>
            )}
          </div>
        </div>

        {/* Right Side - Skills */}
        <div className={styles.skillsContainer}>
          <div className={styles.technologiesAndTools}>
            <div
              className={`${styles.technologiesContainer} ${
                revealed.technologies ? styles.revealed : ''
              }`}
            >
              <h2 className={styles.technologiesTitle}>
                {language === Language.EN ? 'Technologies' : 'Tecnologias'}
              </h2>
              <div
                className={`${styles.technologiesGrid} ${pauseTechnologies ? styles.paused : ''}`}
              >
                {technologies.map((tech, index) => {
                  const totalItems = technologies.length;
                  const angle = (index * 360) / totalItems - 90;
                  const radius = isMobile
                    ? TECH_RADIUS_MOBILE
                    : TECH_RADIUS_DESKTOP;
                  const x = Math.cos((angle * Math.PI) / 180) * radius;
                  const y = Math.sin((angle * Math.PI) / 180) * radius;

                  return (
                    <div
                      key={tech.name}
                      className={styles.technology}
                      data-tooltip={tech.name}
                      style={{
                        left: `calc(50% + ${x}px)`,
                        top: `calc(50% + ${y}px)`,
                        transform: 'translate(-50%, -50%)',
                      }}
                      onMouseEnter={(e) =>
                        handleEnter(e, tech.name, setPauseTechnologies)
                      }
                      onMouseLeave={() => handleLeave(setPauseTechnologies)}
                      onTouchStart={(e) =>
                        handleEnter(e, tech.name, setPauseTechnologies)
                      }
                    >
                      <div className={styles.iconWrap}>
                        <ImageComponent
                          name={tech.name}
                          img={tech.icon}
                          desc={tech.name}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div
              className={`${styles.toolsContainer} ${
                revealed.tools ? styles.revealed : ''
              }`}
            >
              <h2 className={styles.toolsTitle}>
                {language === Language.EN ? 'Tools' : 'Ferramentas'}
              </h2>
              <div
                className={`${styles.toolsGrid} ${pauseTools ? styles.paused : ''}`}
              >
                {tools.map((tool, index) => {
                  const totalItems = tools.length;
                  const angle = (index * 360) / totalItems - 90;
                  const radius = isMobile
                    ? TOOLS_RADIUS_MOBILE
                    : TOOLS_RADIUS_DESKTOP;
                  const x = Math.cos((angle * Math.PI) / 180) * radius;
                  const y = Math.sin((angle * Math.PI) / 180) * radius;

                  return (
                    <div
                      key={tool.name}
                      className={styles.tool}
                      data-tooltip={tool.name}
                      style={{
                        left: `calc(50% + ${x}px)`,
                        top: `calc(50% + ${y}px)`,
                        transform: 'translate(-50%, -50%)',
                      }}
                      onMouseEnter={(e) =>
                        handleEnter(e, tool.name, setPauseTools)
                      }
                      onMouseLeave={() => handleLeave(setPauseTools)}
                      onTouchStart={(e) =>
                        handleEnter(e, tool.name, setPauseTools)
                      }
                    >
                      <div className={styles.iconWrap}>
                        <ImageComponent
                          name={tool.name}
                          img={tool.icon}
                          desc={tool.name}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div
            className={`${styles.credentialsContainer} ${revealed.certifications ? styles.revealed : ''}`}
            data-reveal="certifications"
          >
            <div className={styles.certificationsContainer}>
              <h2 className={styles.certificationsTitle}>
                {language === Language.EN ? 'Certifications' : 'Certificações'}
              </h2>
              <div className={styles.certificationsGrid}>
                {Object.entries(credentialsData).map(([key, certification]) => (
                  <div
                    key={certification.name}
                    className={`${styles.certification} ${
                      key === 'BACHELORS_COMPUTER_SCIENCE' ||
                      key === 'GDPR' ||
                      key === 'CIBERSAFE_CITIZEN'
                        ? styles.certificationVariation
                        : ''
                    }`}
                    style={{
                      cursor: certification.document ? 'pointer' : 'default',
                    }}
                    onClick={() => {
                      const doc = certification.document;
                      if (!doc || !doc.img) return;
                      setModalImg(doc.img);
                    }}
                  >
                    <ImageComponent
                      name={certification.name}
                      img={certification.institution.icon}
                      desc={certification.name}
                    />
                    <span>{certification.name}</span>
                    {certification.document && (
                      <div className={styles.openIcon}>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <polyline
                            points="15,3 21,3 21,9"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <line
                            x1="10"
                            y1="14"
                            x2="21"
                            y2="3"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      {modalImg && <Modal img={modalImg} onClose={() => setModalImg(null)} />}
    </div>
  );
};

About.displayName = 'About';
export default About;
